import moment from 'moment-timezone';

// ============================================================
// Team Insights — a small rule engine, not a fixed script.
//
// Each rule inspects real team data and only fires when its condition is
// actually true (e.g. idle time really did increase). buildTeamInsights()
// evaluates every rule, then picks `count` at random from whichever ones
// fired — no priority order, so a fresh page load can surface a different
// mix each time.
//
// Adding more rules later is just appending to TEAM_INSIGHT_RULES below;
// the selection logic does not need to change.
// ============================================================

function pctOf(numerator, denominator) {
  return denominator > 0 ? (numerator / denominator) * 100 : null;
}

// Same UTC-hour → local-label conversion used by buildProductivityOverTime,
// applied to hourly idle seconds to name the specific hour that spiked.
function findPeakIdleHourLabel(today, timezone) {
  const { hourlyTotalSec, hourlyProductiveSec, hourlyIdleSec, dateId } =
    today || {};
  if (!dateId) return null;

  let bestHour = null;
  let bestIdleSec = 0;
  for (let h = 0; h < 24; h++) {
    const idleSec = hourlyIdleSec
      ? Number(hourlyIdleSec[h]) || 0
      : (Number(hourlyTotalSec?.[h]) || 0) -
        (Number(hourlyProductiveSec?.[h]) || 0);
    if (idleSec > bestIdleSec) {
      bestIdleSec = idleSec;
      bestHour = h;
    }
  }
  if (bestHour == null) return null;

  const tz = timezone || moment.tz.guess();
  return moment
    .utc(`${dateId}T${String(bestHour).padStart(2, '0')}:00:00Z`)
    .tz(tz)
    .format('h A');
}

// ── Rules ────────────────────────────────────────────────────────────────
// Each rule: { id, evaluate(ctx) => Insight|null }

export const TEAM_INSIGHT_RULES = [
  {
    id: 'productivity-trend',
    evaluate: ({ today, yesterday }) => {
      const todayPct = today?.productivityPercentage;
      const yesterdayPct = yesterday?.productivityPercentage;
      if (todayPct == null || yesterdayPct == null) return null;

      const deltaPp = Math.round(todayPct - yesterdayPct);
      if (deltaPp === 0) return null;

      const up = deltaPp > 0;
      return {
        id: 'productivity-trend',
        iconType: up ? 'trending-up' : 'warning',
        iconBg: up ? '#ECFDF3' : '#FFF6ED',
        iconColor: up ? '#22c55e' : '#f59e0b',
        title: `Productivity ${up ? 'increased' : 'decreased'} by ${Math.abs(deltaPp)}%`,
        desc: `Team productivity is ${up ? 'higher' : 'lower'} than yesterday.`,
      };
    },
  },
  {
    id: 'idle-time-trend',
    evaluate: ({ today, yesterday, timezone }) => {
      const todayIdlePct = pctOf(today?.idleSec, today?.totalSec);
      const yesterdayIdlePct = pctOf(yesterday?.idleSec, yesterday?.totalSec);
      if (todayIdlePct == null || yesterdayIdlePct == null) return null;

      const deltaPp = Math.round(todayIdlePct - yesterdayIdlePct);
      if (deltaPp === 0) return null;

      const up = deltaPp > 0;
      const peakHour = up ? findPeakIdleHourLabel(today, timezone) : null;

      return {
        id: 'idle-time-trend',
        // Amber is Idle's identity color everywhere else in the app (Key
        // Highlights, Activity Breakdown) — keep it for both directions here
        // too, instead of switching to green, which is Productivity's color.
        iconType: up ? 'alert' : 'trending-down',
        iconBg: '#FFF6ED',
        iconColor: '#f59e0b',
        title: `Idle time ${up ? 'increased' : 'decreased'} by ${Math.abs(deltaPp)}%`,
        desc: peakHour
          ? `Idle time spiked around ${peakHour} today.`
          : `Team idle time is ${up ? 'higher' : 'lower'} than yesterday.`,
      };
    },
  },
  {
    id: 'offline-time-trend',
    evaluate: ({ today, yesterday }) => {
      // "Offline" = the gap between login and last heartbeat, minus tracked
      // time — same span-based % used by the Activity Breakdown donut, so
      // this number matches what a user would see if they checked that card.
      const todaySpan = (today?.totalSec || 0) + (today?.offlineSec || 0);
      const yesterdaySpan =
        (yesterday?.totalSec || 0) + (yesterday?.offlineSec || 0);
      const todayOfflinePct = pctOf(today?.offlineSec, todaySpan);
      const yesterdayOfflinePct = pctOf(yesterday?.offlineSec, yesterdaySpan);
      if (todayOfflinePct == null || yesterdayOfflinePct == null) return null;

      const deltaPp = Math.round(todayOfflinePct - yesterdayOfflinePct);
      if (deltaPp === 0) return null;

      const up = deltaPp > 0;
      return {
        id: 'offline-time-trend',
        // Gray matches Offline's identity color on the Activity Breakdown
        // donut — distinct from Idle (amber), Alerts (red), etc.
        iconType: up ? 'alert' : 'trending-down',
        iconBg: '#F3F4F6',
        iconColor: '#6b7280',
        title: `Offline time ${up ? 'increased' : 'decreased'} by ${Math.abs(deltaPp)}%`,
        desc: `Team was ${up ? 'more' : 'less'} disconnected than yesterday.`,
      };
    },
  },
  {
    id: 'top-performer',
    evaluate: ({ top5Performers, memberDetails }) => {
      const leader = top5Performers?.[0];
      if (!leader) return null;

      const name = memberDetails?.[leader.userUID]?.userName || 'A team member';
      const score = Math.round(leader.productivityPct || 0);

      return {
        id: 'top-performer',
        iconType: 'star',
        iconBg: '#EEF4FF',
        iconColor: '#4F7EF8',
        title: 'Top performer today',
        desc: `${name} has the highest productivity at ${score}%.`,
      };
    },
  },
  {
    id: 'attendance-trend',
    evaluate: ({ today, yesterday }) => {
      const todayPct = pctOf(today?.presentCount, today?.membersCount);
      const yesterdayPct = pctOf(
        yesterday?.presentCount,
        yesterday?.membersCount
      );
      if (todayPct == null || yesterdayPct == null) return null;

      const deltaPp = Math.round(todayPct - yesterdayPct);
      if (deltaPp === 0) return null;

      const up = deltaPp > 0;
      return {
        id: 'attendance-trend',
        // Green is Attendance's identity color elsewhere (its own donut) —
        // keep it for both directions, vary only the icon shape, same as Idle.
        iconType: up ? 'trending-up' : 'trending-down',
        iconBg: '#ECFDF3',
        iconColor: '#22c55e',
        title: `Attendance ${up ? 'improved' : 'dropped'} by ${Math.abs(deltaPp)}%`,
        desc: `Team attendance is ${up ? 'higher' : 'lower'} than yesterday.`,
      };
    },
  },
  {
    id: 'alerts-count-trend',
    evaluate: ({ today, yesterday }) => {
      const todayCount = today?.alertsCount;
      const yesterdayCount = yesterday?.alertsCount;
      if (todayCount == null || yesterdayCount == null) return null;

      const delta = todayCount - yesterdayCount;
      if (delta === 0) return null;

      const up = delta > 0;
      return {
        id: 'alerts-count-trend',
        // Red is Alerts' identity color elsewhere (Key Highlights tile) —
        // reserved for the "more alerts" case; green signals genuine good news.
        iconType: up ? 'warning' : 'trending-down',
        iconBg: up ? '#FFF1F3' : '#ECFDF3',
        iconColor: up ? '#ef4444' : '#22c55e',
        title:
          !up && todayCount === 0
            ? 'No alerts today'
            : `Alerts ${up ? 'increased' : 'decreased'} by ${Math.abs(delta)} today`,
        desc: up
          ? 'More productivity alerts were raised today than yesterday.'
          : 'Fewer productivity alerts today than yesterday.',
      };
    },
  },
  {
    id: 'top-app',
    evaluate: ({ topApplications }) => {
      const topApp = topApplications?.[0];
      if (!topApp || topApp.name === 'Others') return null;

      const pct = Math.round(parseFloat(topApp.pct));
      return {
        id: 'top-app',
        // Matches the color of the #1 tile on the Top Applications card.
        iconType: 'app',
        iconBg: '#F4F3FF',
        iconColor: '#7863fa',
        title: 'Most used application today',
        desc: `${topApp.name} was used the most (${pct}% of tracked time).`,
      };
    },
  },
  {
    id: 'focus-time-trend',
    evaluate: ({ todayFocusPct, yesterdayFocusPct }) => {
      if (todayFocusPct == null || yesterdayFocusPct == null) return null;

      const deltaPp = Math.round(todayFocusPct - yesterdayFocusPct);
      if (deltaPp === 0) return null;

      const up = deltaPp > 0;
      return {
        id: 'focus-time-trend',
        // Blue is Focus Time's identity color elsewhere (Key Highlights tile).
        iconType: up ? 'trending-up' : 'trending-down',
        iconBg: '#EEF4FF',
        iconColor: '#4F7EF8',
        title: `Focus time ${up ? 'increased' : 'decreased'} by ${Math.abs(deltaPp)}%`,
        desc: `Team focus is ${up ? 'higher' : 'lower'} than yesterday.`,
      };
    },
  },
];

function shuffle(items) {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Evaluates every rule against real data and returns `count` picked at
 * random from whichever ones fired — no priority order, so this reshuffles
 * on every call (e.g. every page refresh).
 *
 * @param {{ today, yesterday, last7, top5Performers, memberDetails, timezone, todayFocusPct, yesterdayFocusPct, topApplications }} ctx
 * @param {{ count?: number }} options
 */
export function buildTeamInsights(ctx, { count = 4 } = {}) {
  if (!ctx?.today) return [];

  const fired = TEAM_INSIGHT_RULES.map((rule) => rule.evaluate(ctx)).filter(
    Boolean
  );

  return shuffle(fired).slice(0, count);
}
